// deps
import { useEffect, useState, useCallback, useRef, useContext } from "react";

//internal-deps
import { useMountedRef } from "@swiggy-private/core-ui";
import { ApiContext, GlobalHeaderState, SwgyHeaderKeysList } from "@swiggy-private/checkout-core";

// apis
import { fetchAddMoneyData } from "@payment-client/apis/SwiggyMoney";

// interfaces
import { AddMoneyContextValue, AddMoneyDetailsState } from "@payment-client/contexts/AddSwiggyMoneyContext";

//helpers
import { getAdditionalRechargeRequired } from "@payment-client/helpers/SwiggyInstrumentsHelper";

const useAddSwiggyMoneyDetails = (): AddMoneyContextValue => {
    const isMountedRef = useMountedRef();
    const apiContextValue = useContext(ApiContext);
    const apiCallRef = useRef(false);
    const apiHost = apiContextValue.host.unwrap();
    const paymentHost = apiContextValue.paymentHost.unwrap();

    const [originalBillAmount, setOriginalBillAmount] = useState(0);
    const [data, setData] = useState<AddMoneyDetailsState>({
        fetchDataInProgress: false,
    });
    const [selectedAmount, setSelectedAmount] = useState(0);

    const getAddMoneyRequestData = useCallback(
        (billAmount?: number) => {
            if (isMountedRef.current && !apiCallRef.current && !data.addMoneyData) {
                setOriginalBillAmount(billAmount || 0);
                setData({
                    fetchDataInProgress: true,
                    criticalError: {
                        error: false,
                        message: "",
                    },
                });
            }
        },
        [isMountedRef, data.addMoneyData, setData],
    );

    const updateSelectedAmount = useCallback(
        (amount: number) => {
            setSelectedAmount(amount);
        },
        [setSelectedAmount],
    );

    useEffect(() => {
        if (!isMountedRef.current || !data.fetchDataInProgress || apiCallRef.current) return;

        apiCallRef.current = true;

        /*
         *  Need to send bill total as query param, as this will be used by
         *  backend to auto-select relevant suggestion based on bill total shown to the user
         */
        fetchAddMoneyData(String(originalBillAmount), paymentHost)
            .then(payload => {
                apiCallRef.current = false;
                const suggestedAmounts = payload.eligibilityDetails.suggestedAmounts ?? [];
                const defaultSelection = suggestedAmounts.find(amount => amount.preSelected);
                const originalAmount = GlobalHeaderState.getHeader(SwgyHeaderKeysList.paymentamount);
                const additionalRechargeRequired = getAdditionalRechargeRequired(
                    originalBillAmount,
                    payload.eligibleBalance,
                );
                const selectionAmount =
                    Number(originalAmount) || defaultSelection?.amount || additionalRechargeRequired || 0;
                setSelectedAmount(selectionAmount);
                setData({ fetchDataInProgress: false, addMoneyData: payload });
            })
            .catch(er => {
                apiCallRef.current = false;
                setData({
                    fetchDataInProgress: false,
                    criticalError: {
                        error: true,
                        message: er.statusMessage,
                    },
                });
            });
    }, [data.fetchDataInProgress, isMountedRef, apiHost, paymentHost, originalBillAmount]);

    return { data, getAddMoneyRequestData, updateSelectedAmount, selectedAmount, originalBillAmount };
};

export default useAddSwiggyMoneyDetails;
